import type { ComponentType } from 'react';
import { Calendar, Clock, Instagram, Package, Puzzle, type LucideIcon } from 'lucide-react';
import { GenericJsonEditor } from './GenericJsonEditor';
import { OpeningHoursEditor } from './OpeningHoursEditor';
import { SocialMediaFeedEditor } from './SocialMediaFeedEditor';
import { WawiProductsEditor } from './WawiProductsEditor';
import { WawiCoursesEditor } from './WawiCoursesEditor';
import { DAYS_ORDERED, type EditorProps, type OpeningHoursConfig, type SocialMediaFeedConfig, type WawiProductsConfig, type WawiCoursesConfig } from './types';

/* ─── Meta (icon, label, color) ────────────────────────────── */

export interface WidgetMeta {
  icon: LucideIcon;
  label: string;
  color: string;
}

export const widgetMeta: Record<string, WidgetMeta> = {
  openingHours: { icon: Clock, label: 'Öffnungszeiten', color: 'text-amber-400' },
  socialMediaFeed: { icon: Instagram, label: 'Social Media Feed', color: 'text-pink-400' },
  wawiProducts: { icon: Package, label: 'WaWi-Produkte', color: 'text-emerald-400' },
  wawiCourses: { icon: Calendar, label: 'WaWi-Kurse', color: 'text-sky-400' },
};

export const fallbackMeta: WidgetMeta = {
  icon: Puzzle,
  label: 'Widget',
  color: 'text-neutral-400',
};

export function getWidgetMeta(type: string): WidgetMeta {
  return widgetMeta[type] ?? fallbackMeta;
}

/* ─── Editor registry ──────────────────────────────────────── */

type AnyEditor = ComponentType<EditorProps<Record<string, unknown>>>;

const editors: Record<string, AnyEditor> = {
  openingHours: OpeningHoursEditor as unknown as AnyEditor,
  socialMediaFeed: SocialMediaFeedEditor as unknown as AnyEditor,
  wawiProducts: WawiProductsEditor as unknown as AnyEditor,
  wawiCourses: WawiCoursesEditor as unknown as AnyEditor,
};

export function getEditor(type: string): AnyEditor {
  return editors[type] ?? (GenericJsonEditor as AnyEditor);
}

/* ─── Default configs ──────────────────────────────────────── */

function defaultOpeningHours(): OpeningHoursConfig {
  const regular: OpeningHoursConfig['regularHours'] = {};
  for (const { key, day, dayShort } of DAYS_ORDERED) {
    if (key === 'saturday' || key === 'sunday') {
      regular[key] = { day, dayShort, closed: true };
    } else {
      regular[key] = {
        day,
        dayShort,
        periods: [{ open: '09:00', close: '18:00' }],
      };
    }
  }
  return { regularHours: regular, specialClosures: [] };
}

function defaultSocialMediaFeed(): SocialMediaFeedConfig {
  return {
    platform: 'instagram',
    handle: '',
    count: 6,
    refreshIntervalMinutes: 60,
    showInHero: true,
    showAsGrid: true,
    heroCarousel: {
      useHeroImageAsFirstSlide: true,
      autoplay: true,
      intervalSeconds: 6,
      showCaptions: true,
    },
  };
}

function defaultWawiProducts(): WawiProductsConfig {
  return {
    title: 'Unsere Produkte',
    subtitle: '',
    maxProducts: 12,
    layout: 'grid',
    showPrice: true,
    search: '',
  };
}

function defaultWawiCourses(): WawiCoursesConfig {
  return {
    title: 'Unsere Kurse',
    subtitle: '',
    maxCourses: 20,
    showPrice: true,
  };
}

export function getDefaultConfig(widgetType: string): Record<string, unknown> {
  switch (widgetType) {
    case 'openingHours':
      return defaultOpeningHours() as unknown as Record<string, unknown>;
    case 'socialMediaFeed':
      return defaultSocialMediaFeed() as unknown as Record<string, unknown>;
    case 'wawiProducts':
      return defaultWawiProducts() as unknown as Record<string, unknown>;
    case 'wawiCourses':
      return defaultWawiCourses() as unknown as Record<string, unknown>;
    default:
      return {};
  }
}
